/**
 * Minimap (doc 17 §3): 132×112 UI px, bottom-right. Terrain baked once; live
 * dots redraw at ≤15 Hz; a rim ring outside the map bounds carries the
 * circling shoals so the pull picture survives at any zoom; the viewport rect
 * and click-to-jump make it navigation, not decoration.
 */

import type { Camera, Viewport } from '../render/camera.ts';
import { screenToTile } from '../render/iso.ts';
import { OUTLINE_HOVER, SEMANTIC, type DepthPalette } from '../render/palette.ts';
import { hex, mix, PixelSurface, shade } from '../render/surface.ts';
import { T_ROCK, T_VEIN, T_VENT, type SimGrid } from '../sim/grid.ts';
import { SPECIES } from '../sim/species.ts';
import type { Sim } from '../sim/sim.ts';
import type { MapDef, RimShoal } from '../sim/types.ts';

export const MINI_W = 132;
export const MINI_H = 112;
const CX = 66;
const CY = 10;
const SX = 1.5;
const SY = 1.15;

function miniX(i: number, j: number): number {
  return CX + Math.round((i - j) * SX);
}
function miniY(i: number, j: number): number {
  return CY + Math.round((i + j) * SY);
}

export class Minimap {
  readonly surface: PixelSurface;
  private readonly terrain: PixelSurface;

    private readonly map: MapDef;
  private readonly depth: DepthPalette;

  constructor(
    grid: SimGrid,
    map: MapDef,
    depth: DepthPalette,
  ) {    this.map = map;
    this.depth = depth;
    this.surface = new PixelSurface(MINI_W, MINI_H);
    this.terrain = new PixelSurface(MINI_W, MINI_H);
    const t = this.terrain;
    t.clear(hex('#03121a'));
    t.strokeRect(0, 0, MINI_W, MINI_H, hex('#1c3a42'));
    for (let i = 0; i < map.size; i++) {
      for (let j = 0; j < map.size; j++) {
        const k = grid.terrain[grid.idx(i, j)]!;
        const c =
          k === T_ROCK
            ? shade(depth.terrain, 0.5)
            : k === T_VEIN
              ? hex('#93a6ab')
              : k === T_VENT
                ? shade(depth.playerGlow, 0.7)
                : shade(depth.terrain, 0.9);
        t.px(miniX(i, j), miniY(i, j), c, 'set');
      }
    }
    // spawn edges glow on the outline (doc 17: they pulse during a wave — the
    // pulse arrives with the alerts pass)
    const glow = shade(depth.enemyGlow, 0.55);
    for (const edge of map.spawnEdges) {
      for (let a = 0; a < map.size; a += 2) {
        const [i, j] =
          edge === 'e' ? [map.size - 1, a] : edge === 'w' ? [0, a] : edge === 'n' ? [a, 0] : [a, map.size - 1];
        t.px(miniX(i!, j!), miniY(i!, j!), glow, 'set');
      }
    }
  }

  /** Where the minimap sits in UI space (bottom-right, doc 17 §3). */
  rect(uiW: number, uiH: number): { x: number; y: number; w: number; h: number } {
    return { x: uiW - MINI_W - 8, y: uiH - MINI_H - 8, w: MINI_W, h: MINI_H };
  }

  /**
   * `pulse` is the onboarding target (doc 19 §The two gates): the shoal G2
   * points at gets a blinking ring on the rim ring, so the minimap dot and the
   * world both say the same thing.
   */
  redraw(
    sim: Sim,
    camera: Camera,
    viewport: Viewport,
    selectedShoalId: string | null,
    pulse: { shoalId: string; on: boolean } | null = null,
    pings: Array<{ x: number; y: number; severity: 'normal' | 'loud' }> = [],
  ): void {
    const s = this.surface;
    s.clear();
    s.blit(this.terrain, 0, 0, 'set');

    // your buildings as warm dots, the Core ringed and loud
    for (const st of sim.state.structures) {
      if (st.ruin) continue;
      const x = miniX(st.i, st.j);
      const y = miniY(st.i, st.j);
      if (st.def === 'core_dome') {
        s.fillRect(x - 1, y - 1, 3, 3, this.depth.structureEmissive, 'set');
        s.strokeRect(x - 2, y - 2, 5, 5, shade(this.depth.structureEmissive, 0.6));
      } else {
        s.px(x, y, st.kind === 'tower' ? hex('#ff7a3d') : this.depth.structureEmissive, 'set');
      }
    }
    // enemies in cold/toxic light; bosses get 2×2
    for (const e of sim.state.enemies) {
      const x = miniX(e.x, e.y);
      const y = miniY(e.x, e.y);
      if (SPECIES[e.species]!.boss) s.fillRect(x - 1, y - 1, 2, 2, SEMANTIC.critical, 'set');
      else s.px(x, y, this.depth.enemyGlow, 'set');
    }
    // the rim ring: circling shoals as dots coloured by menace
    for (const shoal of sim.state.rim) {
      if (shoal.state !== 'circling') continue;
      const p = this.ringPoint(shoal.orbit);
      const heat = shoal.riled ? 1 : (shoal.menace - 1) / 4;
      const c = shoal.riled
        ? SEMANTIC.critical
        : mix(this.depth.enemyGlow, SEMANTIC.critical, heat * 0.6);
      s.fillRect(p.x - 1, p.y - 1, 2, 2, c, 'set');
      if (shoal.id === selectedShoalId) s.strokeRect(p.x - 3, p.y - 3, 6, 6, OUTLINE_HOVER);
      if (pulse?.on && shoal.id === pulse.shoalId) s.strokeRect(p.x - 4, p.y - 4, 8, 8, SEMANTIC.good);
      if (shoal.boss) s.px(p.x, p.y - 2, SEMANTIC.ink, 'set');
    }
    // doc 17 §4: a pulsing ring wherever something is being alerted about
    for (const p of pings) {
      const x = miniX(p.x, p.y);
      const y = miniY(p.x, p.y);
      const c = p.severity === 'loud' ? SEMANTIC.critical : SEMANTIC.warning;
      s.strokeRect(x - 3, y - 3, 7, 7, c);
      s.strokeRect(x - 1, y - 1, 3, 3, c);
    }

    // viewport rect
    const view = camera.viewInternal(viewport);
    const a = screenToTile(camera.x, camera.y);
    const b = screenToTile(camera.x + view.w, camera.y + view.h);
    const x0 = Math.max(1, Math.min(miniX(a.x, a.y), miniX(b.x, a.y)));
    const y0 = Math.max(1, miniY(a.x, a.y));
    const x1 = Math.min(MINI_W - 2, Math.max(miniX(b.x, b.y), miniX(a.x, b.y)));
    const y1 = Math.min(MINI_H - 2, miniY(b.x, b.y));
    s.strokeRect(x0, y0, Math.max(4, x1 - x0), Math.max(4, y1 - y0), hex('#e8f4f2'));
  }

  /** The rim ring sits just outside the map diamond. */
  private ringPoint(orbit: number): { x: number; y: number } {
    const side = Math.floor(orbit * 4) % 4;
    const along = (orbit * 4) % 1;
    const n = this.map.size - 1;
    const a = along * n;
    const off = 4;
    let i: number;
    let j: number;
    if (side === 0) {
      i = a;
      j = -off;
    } else if (side === 1) {
      i = n + off;
      j = a;
    } else if (side === 2) {
      i = n - a;
      j = n + off;
    } else {
      i = -off;
      j = n - a;
    }
    return {
      x: Math.max(2, Math.min(MINI_W - 3, miniX(i, j))),
      y: Math.max(2, Math.min(MINI_H - 3, miniY(i, j))),
    };
  }

  /** Click/drag → world coordinates to centre on. */
  worldFromMini(mx: number, my: number): { x: number; y: number } {
    const u = (mx - CX) / SX;
    const v = (my - CY) / SY;
    const i = (u + v) / 2;
    const j = (v - u) / 2;
    return { x: (i - j) * 16, y: (i + j) * 8 + 8 };
  }

  /** A circling shoal's ring dot near (mx, my), for hover cards and clicks. */
  shoalAt(sim: Sim, mx: number, my: number): RimShoal | null {
    for (const shoal of sim.state.rim) {
      if (shoal.state !== 'circling') continue;
      const p = this.ringPoint(shoal.orbit);
      if (Math.abs(mx - p.x) <= 3 && Math.abs(my - p.y) <= 3) return shoal;
    }
    return null;
  }
}
